import React from 'react'
import { cn } from './cn'

interface FieldContextValue {
  invalid: boolean
  /** Explicit label target for a one-control Field, if the caller supplied one. */
  controlId?: string
  /** Generated label ID for Field controls without an explicit `htmlFor`. */
  labelId?: string
  descriptionId?: string
  descriptionIds?: string
}

const FieldContext = React.createContext<FieldContextValue>({ invalid: false })

export const useFieldInvalid = () => React.useContext(FieldContext).invalid

/** The active Field error or hint description, for controls with their own DOM node. */
export const useFieldA11y = () => React.useContext(FieldContext)

/** Combines caller-provided IDs with Field's active hint/error description. */
export function mergeAriaDescribedBy(
  explicit: string | undefined,
  fieldDescriptionId: string | undefined
): string | undefined {
  const ids = [
    ...new Set([explicit, fieldDescriptionId].flatMap((value) => value?.split(/\s+/) ?? [])),
  ]
    .map((id) => id.trim())
    .filter(Boolean)
  return ids.length > 0 ? ids.join(' ') : undefined
}

/** Combines a caller-provided accessible name with Field's visible label. */
export function mergeAriaLabelledBy(
  explicit: string | undefined,
  fieldLabelId: string | undefined
): string | undefined {
  const ids = [...new Set([explicit, fieldLabelId].flatMap((value) => value?.split(/\s+/) ?? []))]
    .map((id) => id.trim())
    .filter(Boolean)
  return ids.length > 0 ? ids.join(' ') : undefined
}

export interface FieldProps {
  label?: React.ReactNode
  htmlFor?: string
  /** 出錯時套用 Open Design 的 `.invalid`／`.ferr` 欄位錯誤契約。 */
  error?: string | null
  /** 欄位規則說明；出錯時會由 error 取代，避免重複宣告。 */
  hint?: React.ReactNode
  /** 傳給 error 節點，供控制項以 aria-describedby 連結。 */
  errorId?: string
  /** 傳給 hint 節點，供控制項以 aria-describedby 連結。 */
  hintId?: string
  className?: string
  children: React.ReactNode
}

export function Field({
  label,
  htmlFor,
  error,
  hint,
  errorId,
  hintId,
  className,
  children,
}: FieldProps) {
  const invalid = Boolean(error)
  const generatedLabelId = React.useId()
  const generatedDescriptionId = React.useId()
  const controlId = htmlFor
  const labelId = label != null && htmlFor == null ? generatedLabelId : undefined
  const resolvedHintId =
    hint != null && !error ? (hintId ?? `${generatedDescriptionId}-hint`) : undefined
  const resolvedErrorId = error ? (errorId ?? `${generatedDescriptionId}-error`) : undefined
  const descriptionId = resolvedErrorId ?? resolvedHintId
  const descriptionIds = descriptionId

  return (
    <FieldContext.Provider value={{ invalid, controlId, labelId, descriptionId, descriptionIds }}>
      <div className={cn('field', invalid && 'invalid', className)}>
        {label != null ? (
          <label id={labelId} htmlFor={htmlFor}>
            {label}
          </label>
        ) : null}
        {children}
        {hint != null && !error ? (
          <p id={resolvedHintId} className="field-hint">
            {hint}
          </p>
        ) : null}
        {error ? (
          <p id={resolvedErrorId} className="ferr" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  )
}

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>

export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    className,
    id,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    'aria-labelledby': ariaLabelledBy,
    ...rest
  },
  ref
) {
  const generatedId = React.useId()
  const { controlId, invalid, labelId, descriptionIds } = useFieldA11y()

  return (
    <input
      ref={ref}
      id={id ?? controlId ?? generatedId}
      aria-invalid={ariaInvalid ?? (invalid || undefined)}
      aria-describedby={mergeAriaDescribedBy(ariaDescribedBy, descriptionIds)}
      aria-labelledby={mergeAriaLabelledBy(ariaLabelledBy, labelId)}
      className={cn('input', className)}
      {...rest}
    />
  )
})
